// Three.js:
import * as THREE from 'three';

// Custom:

import { VisualizationElements } from './VisualizationElements.js';

// Store Zustand
import { AllColorScheme, BuildingSource, ContextState, GeoObjectMode, UUID } from '@visualizer/types';
import { getElement, getGlobalState } from '../Gui/store/persistent/persistentStore';
import { getGrid } from '../store/LoadingStore';
import { getViewer } from '../store/viewerStore';
import { GeoObject } from '@data/geodata/legacy/GeoObject.js';
import { GeoEditManager } from '@data/geodata/edit/GeoEditManager';
import { SelectMode } from '@data/geodata/edit/SelectMode.js';

// Density Block:
import { getDensityRenderScale } from '@data/Simulations/pipeline/blocks/DensityBlock';

export class StandardRenderer {
    previousButton: any;
    singleVolumeGenerated: any;

    constructor() {
        // Design Schemes:
        this.singleVolumeGenerated = false;
    }

    // Use GUI status to show or hide context items:
    updateContext() {
        if (getGrid().getActiveLayer() != null) {
            const s = getDensityRenderScale(getElement()?.ELEMENT_CURRENT_DENSITY); // 1 or 3
            VisualizationElements.selectionBox?.scale.set(s, s, s);
        }
        return true;
    }
}

export function initGeodata(colorScheme: AllColorScheme, mode: GeoObjectMode, context: ContextState) {
    const scene: THREE.Scene = getViewer().scene;
    getGrid()
        .getActiveGeodata()
        .getIteratorLegacy()
        .forEach((obj) => {
            if (!obj || !(obj instanceof GeoObject)) return;
            // console.log('\nIm drawing ', obj);

            console.time('Time taken to draw: ');

            if (scene) {
                const result = (obj as GeoObject).initMeshes();

                if ('trianglesMesh' in result) {
                    const { trianglesMesh } = result;

                    scene.add(trianglesMesh);
                }
                if ('strokesMesh' in result) {
                    const { strokesMesh } = result;
                    if (strokesMesh.name != 'Roads' && strokesMesh.name != 'Targets') {
                        scene.add(strokesMesh);
                    }
                }
            }

            console.timeEnd('Time taken to draw: ');
            console.log('\nFinished drawing ');
        }); // draw all objects

    const geodata = getGrid().getActiveGeodata();
    geodata.getIterator().forEach((g) => {
        g.setMode(mode);
        g.changeColorScheme(colorScheme);
        scene.add(g);
    });
    geodata.dataObject.Buildings?.setVisible(context?.showBuildings);
    geodata.dataObject.Buildings?.setBuildingSourceVisible(BuildingSource.CUSTOM_OBS, context?.showCustomObstruction);
    geodata.dataObject.Buildings?.setBuildingSourceVisible(BuildingSource.SKETCH, context?.showProjectSketch);
    geodata.dataObject.Trees?.setVisible(context?.showTrees);
    getGrid().contextItems?.syncTerrainLines();

    // Setup geo editing hooks (hover + manager)
    const viewer = getViewer();
    viewer.selectMode?.disable();

    const geoEditManager = GeoEditManager.createFromGeodata(geodata, async () => {
        geodata.dataObject.Buildings?.rebuildMergedEdges();
        geodata.dataObject.Trees?.rebuildMergedEdges();
    });

    if (!geoEditManager) {
        viewer.geoEditManager = undefined;
        viewer.selectMode = undefined;
        return;
    }

    viewer.geoEditManager = geoEditManager;
    viewer.selectMode = new SelectMode(
        viewer.cameraHelper.getActiveCamera(getGlobalState().ambient.currentCamera.type),
        viewer.renderer.domElement,
        geodata.dataObject.Buildings ?? null,
        geodata.dataObject.Trees ?? null,
        geoEditManager
    );
}

export function unloadGeodata(id: UUID) {
    // This function will crash when trees or buildings or roads are null. In that case try to investigate on
    // what is inside dataObject and maybe iterating it and calling a dispose?
    getGrid()
        .getAllGeodatas()
        .find((g) => g.id === id)!
        .getIteratorLegacy()
        .forEach((value) => {
            if (value) {
                value.dispose();
            }
        });

    const scene: THREE.Scene = getViewer().scene;
    getGrid()
        .getAllGeodatas()
        .find((g) => g.id === id)!
        .getIterator()
        .forEach((value) => {
            scene.remove(value);
        });
}

export function refreshContext(colorScheme: AllColorScheme, mode: GeoObjectMode, context: ContextState) {
    const geodataId = getGrid().getActiveGeodata().id;
    unloadGeodata(geodataId);
    initGeodata(colorScheme, mode, context);
}

export function createTrianglesMesh(vertices: Float32Array, indices: number[], meshColor: number = 0xe1e1e1) {
    // Create geometry:
    const geometry = new THREE.BufferGeometry();
    geometry.setIndex(indices);
    geometry.setAttribute('position', new THREE.Float32BufferAttribute(vertices, 3));

    // Generate mesh:
    let material = new THREE.MeshBasicMaterial({
        color: meshColor,
        side: THREE.DoubleSide,
        polygonOffset: true,
        depthTest: true,
        polygonOffsetFactor: 1,
        polygonOffsetUnits: 1,
    });
    let mesh = new THREE.Mesh(geometry, material);

    return mesh;
}

export function createMesh(vertices: Float32Array, trianglesIndices: number[], material: THREE.Material | THREE.Material[]): any {
    // Create geometry:
    const geometry = new THREE.BufferGeometry();
    geometry.setIndex(trianglesIndices);
    geometry.setAttribute('position', new THREE.Float32BufferAttribute(vertices, 3).setUsage(THREE.DynamicDrawUsage));
    // geometry.setAttribute( 'position', new THREE.BufferAttribute(vertices, 3).setUsage( THREE.DynamicDrawUsage) ) ;

    // Generate mesh:
    geometry.computeVertexNormals(); //<-- this
    geometry.normalizeNormals();
    let mesh = new THREE.Mesh(geometry, material);
    mesh.castShadow = true;
    mesh.receiveShadow = true;

    return mesh;
}

export function createLinesMesh(vertices: Float32Array, indices: number[], strokeColor: number = 0xff0000): any {
    const { lowPerformance } = getGlobalState().settings;
    let activateShadow = false;

    // Add edge lines for every triangle:
    const geometry = new THREE.BufferGeometry();
    geometry.setIndex(indices);
    geometry.setAttribute('position', new THREE.Float32BufferAttribute(vertices, 3));

    // const newEdges = createCustomEdgesGeometry(geometry, strokeColor);
    const edgeGeometry = lowPerformance ? THREE.EdgesGeometry : THREE.WireframeGeometry;
    const edges = new edgeGeometry(geometry, 1);
    const lineMaterial = new THREE.LineBasicMaterial({
        color: strokeColor,
        linewidth: 1,
    });
    const newEdges = new THREE.LineSegments(edges, lineMaterial);
    newEdges.renderOrder = 3;
    lineMaterial.depthTest = true;
    lineMaterial.depthWrite = false;
    lineMaterial.polygonOffset = true;
    lineMaterial.polygonOffsetFactor = -3;
    lineMaterial.polygonOffsetUnits = -3;

    if (activateShadow) newEdges.visible = false;

    return newEdges;
}

// TODO: Update the function above to return LineSegments2. And then test the effect of having the lines of the terrain projected.

// function createLinesMesh(vertices: Float32Array, colorNumber: number, linewidth: number = 2): THREE.Mesh {
//     const color: THREE.Color = new THREE.Color(colorNumber);

//     const geometry = new LineSegmentsGeometry();
//     geometry.setPositions(vertices);

//     const material = new LineMaterial({
//         color,
//         linewidth,
//         resolution: new THREE.Vector2(window.innerWidth * window.devicePixelRatio, window.innerHeight * window.devicePixelRatio),
//     });

//     const line = new LineSegments2(geometry, material);
//     return line;
// }
